import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import homeApi from '../api/homeApi';

export type Business = { slug: string; name: string };

type Ctx = {
  businesses: Business[];
  selected: Business | null;
  setSelected: (b: Business | null) => void;
};

const BusinessContext = createContext<Ctx | null>(null);

const LS_KEY = 'finz_selected_business';

export const BusinessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [businesses, setBusinesses] = useState<Business[]>([{ slug: 'the_winslow', name: 'Winslow' }]);
  const [selected, setSelectedState] = useState<Business | null>(() => {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      try {
        const s = JSON.parse(raw) as Business;
        return s;
      } catch { }
    }
    return null;
  });

  useEffect(() => {
    const fetch = async () => {
      try {
        const list = await homeApi.getBusinesses();
        if (list.length > 0) {
          setBusinesses(list);
          // If currently selected is not in list, and it's not null, default to first or keep null
          if (selected && !list.find(b => b.slug === selected.slug)) {
            // If it was selected but now disappeared from list, we can keep it or clear it
            // For now, let's just keep it if it was from LS, or clear it if invalid
          }
        }
      } catch (e) {
        console.error("Failed to load businesses", e);
      }
    };
    fetch();
  }, []); // Run once on mount

  const setSelected = (b: Business | null) => {
    setSelectedState(b);
    if (b) {
      localStorage.setItem(LS_KEY, JSON.stringify(b));
    } else {
      localStorage.removeItem(LS_KEY);
    }
  };

  const value = useMemo(() => ({ businesses, selected, setSelected }), [businesses, selected]);

  return <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>;
};

export const useBusiness = () => {
  const ctx = useContext(BusinessContext);
  if (!ctx) throw new Error('useBusiness must be used inside <BusinessProvider>');
  return ctx;
};
